/**
 * Enhanced Knowledge Base Uploader - RAG Processing Integration
 * Simple, intuitive interface for document upload with real-time RAG processing visualization
 * Following Cosellus8 UX patterns for professional user experience
 */

import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Progress } from '../ui/Progress';
import { 
  Upload, FileText, Database, Brain, CheckCircle2, 
  AlertCircle, Clock, Eye, Trash2, Search, Filter,
  Layers, Network, Zap, BarChart3
} from 'lucide-react';

// Enhanced types for RAG processing
interface RAGProcessingStep {
  name: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  progress: number;
  details?: string;
  duration?: number;
}

interface KnowledgeDocument {
  id: string;
  filename: string;
  size: number;
  type: string;
  uploadedAt: Date;
  status: 'uploading' | 'processing' | 'completed' | 'error';
  
  // RAG Processing Pipeline
  ragProcessing: {
    textExtraction: RAGProcessingStep;
    chunking: RAGProcessingStep;
    embedding: RAGProcessingStep;
    vectorStorage: RAGProcessingStep;
  };
  
  // Results
  chunks: number;
  embeddings: number;
  vectorCollection: string;
  categories: string[];
  agentAccess: string[];
}

interface VectorStoreStats {
  totalCollections: number;
  totalDocuments: number;
  totalEmbeddings: number;
  totalAgents: number;
  storageSize: string;
  avgProcessingTime: string;
}

interface EnhancedKnowledgeUploaderProps {
  organizationId: string;
  onDocumentProcessed?: (document: KnowledgeDocument) => void;
  onError?: (error: string) => void;
}

export const EnhancedKnowledgeUploader: React.FC<EnhancedKnowledgeUploaderProps> = ({
  organizationId,
  onDocumentProcessed,
  onError
}) => {
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'upload' | 'processing' | 'management'>('upload');
  const [stats, setStats] = useState<VectorStoreStats>({
    totalCollections: 3,
    totalDocuments: 127,
    totalEmbeddings: 15420,
    totalAgents: 8,
    storageSize: '2.3 GB',
    avgProcessingTime: '45 sec'
  });
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Simulate RAG processing pipeline
  const processDocument = async (file: File) => {
    const doc: KnowledgeDocument = {
      id: `doc_${Date.now()}`,
      filename: file.name,
      size: file.size,
      type: file.type,
      uploadedAt: new Date(),
      status: 'uploading',
      ragProcessing: {
        textExtraction: { name: 'Text Extraction', status: 'pending', progress: 0 },
        chunking: { name: 'Smart Chunking', status: 'pending', progress: 0 },
        embedding: { name: 'Vector Embeddings', status: 'pending', progress: 0 },
        vectorStorage: { name: 'ChromaDB Storage', status: 'pending', progress: 0 }
      },
      chunks: 0,
      embeddings: 0,
      vectorCollection: `org_${organizationId}`,
      categories: [],
      agentAccess: []
    };

    setDocuments(prev => [doc, ...prev]);
    setViewMode('processing');

    // Simulate processing steps
    setTimeout(() => simulateRAGStep(doc.id, 'textExtraction'), 500);
  };

  const simulateRAGStep = (docId: string, step: keyof KnowledgeDocument['ragProcessing']) => {
    setDocuments(prev => prev.map(doc => {
      if (doc.id !== docId) return doc;
      
      const updatedDoc = { ...doc };
      const currentStep = updatedDoc.ragProcessing[step];
      
      if (currentStep.status === 'pending') {
        currentStep.status = 'processing';
        
        // Simulate progress
        const progressInterval = setInterval(() => {
          setDocuments(curr => curr.map(d => {
            if (d.id !== docId) return d;
            const progressStep = d.ragProcessing[step];
            progressStep.progress += 10;
            
            if (progressStep.progress >= 100) {
              clearInterval(progressInterval);
              progressStep.status = 'completed';
              progressStep.duration = Math.floor(Math.random() * 15) + 5;
              
              // Move to next step
              const steps = Object.keys(d.ragProcessing) as (keyof typeof d.ragProcessing)[];
              const currentIndex = steps.indexOf(step);
              if (currentIndex < steps.length - 1) {
                setTimeout(() => simulateRAGStep(docId, steps[currentIndex + 1]), 1000);
              } else {
                // Processing complete
                d.status = 'completed';
                d.chunks = Math.floor(Math.random() * 50) + 10;
                d.embeddings = d.chunks * 1536; // OpenAI embedding dimensions
                d.categories = ['Product Info', 'Marketing'];
                d.agentAccess = ['Casey Email', 'Compass Strategy', 'Aria Content'];
                
                onDocumentProcessed?.(d);
              }
            }
            return d;
          }));
        }, 300);
      }
      
      return updatedDoc;
    }));
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    setUploading(true);
    Array.from(files).forEach(file => {
      processDocument(file);
    });
    setUploading(false);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (!files) return;

    setUploading(true);
    Array.from(files).forEach(file => {
      processDocument(file);
    });
    setUploading(false);
  };

  const getOverallProgress = (doc: KnowledgeDocument) => {
    const steps = Object.values(doc.ragProcessing);
    const totalProgress = steps.reduce((sum, step) => sum + step.progress, 0);
    return Math.round(totalProgress / steps.length);
  };

  const renderUploadInterface = () => (
    <div className="space-y-6">
      {/* Vector Store Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-blue-600" />
            Knowledge Base Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.totalDocuments}</div>
              <div className="text-sm text-gray-600">Documents</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{stats.totalEmbeddings.toLocaleString()}</div>
              <div className="text-sm text-gray-600">Embeddings</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{stats.totalAgents}</div>
              <div className="text-sm text-gray-600">Connected Agents</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{stats.storageSize}</div>
              <div className="text-sm text-gray-600">Storage Used</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Area */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-blue-600" />
            Upload Knowledge Documents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Drop files here or click to upload</h3>
            <p className="text-gray-600 mb-4">
              Supports PDF, DOCX, PPTX, TXT, MD - Files will be automatically processed for AI agents
            </p>
            
            <Button disabled={uploading}>
              {uploading ? 'Processing...' : 'Select Files'}
            </Button>
            
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.pptx,.txt,.md"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
          
          <div className="mt-4 text-sm text-gray-600">
            <div className="flex items-center gap-4">
              <span>✅ Automatic text extraction</span>
              <span>✅ Smart chunking</span>
              <span>✅ Vector embeddings</span>
              <span>✅ Agent integration</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderProcessingView = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">RAG Processing Pipeline</h2>
        <Button variant="secondary" onClick={() => setViewMode('upload')}>
          ← Back to Upload
        </Button>
      </div>
      
      {documents.filter(doc => doc.status !== 'completed').map(doc => (
        <Card key={doc.id}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-blue-600" />
                <div>
                  <h3 className="font-semibold">{doc.filename}</h3>
                  <p className="text-sm text-gray-600">{(doc.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
              </div>
              <Badge variant={doc.status === 'completed' ? 'default' : 'secondary'}>
                {doc.status}
              </Badge>
            </div>
            
            <div className="space-y-3">
              {Object.entries(doc.ragProcessing).map(([key, step]) => (
                <div key={key} className="flex items-center gap-3">
                  <div className="w-4 h-4">
                    {step.status === 'completed' ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : step.status === 'processing' ? (
                      <Clock className="h-4 w-4 text-blue-600 animate-spin" />
                    ) : (
                      <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">{step.name}</span>
                      {step.duration && (
                        <span className="text-xs text-gray-500">{step.duration}s</span>
                      )}
                    </div>
                    <Progress value={step.progress} className="h-2" />
                  </div>
                </div>
              ))}
            </div>
            
            {doc.status === 'completed' && (
              <div className="mt-4 p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 mb-2">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="font-semibold">Processing Complete!</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Chunks:</span> {doc.chunks}
                  </div>
                  <div>
                    <span className="font-medium">Embeddings:</span> {doc.embeddings.toLocaleString()}
                  </div>
                  <div>
                    <span className="font-medium">Agents:</span> {doc.agentAccess.length}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderManagementView = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Knowledge Base Management</h2>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setViewMode('upload')}>
            <Upload className="h-4 w-4 mr-2" />
            Upload More
          </Button>
        </div>
      </div>
      
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl mb-2">📄</div>
              <h3 className="font-semibold mb-2">Vector Collections</h3>
              <p className="text-gray-600 text-sm">
                {stats.totalCollections} active collections organizing your knowledge by context and domain.
              </p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">🤖</div>
              <h3 className="font-semibold mb-2">Agent Access</h3>
              <p className="text-gray-600 text-sm">
                {stats.totalAgents} AI agents can now access and use your knowledge for enhanced content generation.
              </p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">⚡</div>
              <h3 className="font-semibold mb-2">Processing Speed</h3>
              <p className="text-gray-600 text-sm">
                Average processing time of {stats.avgProcessingTime} per document with real-time updates.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-600" />
              Connected Agents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {['Casey Email Agent', 'Compass Strategy Agent', 'Aria Content Editor', 'Atlas Knowledge Graph', 'Brain Discovery Engine', 'Riley Research Agent'].map(agent => (
                <div key={agent} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm font-medium">{agent}</span>
                  <Badge variant="default" className="bg-green-100 text-green-800">Active</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span>Product_Guide_2024.pdf processed successfully</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span>Marketing_Strategy.docx added to knowledge base</span>
              </div>
              <div className="flex items-center gap-2">
                <Brain className="h-4 w-4 text-purple-600" />
                <span>Casey Email generated content using new knowledge</span>
              </div>
              <div className="flex items-center gap-2">
                <Network className="h-4 w-4 text-blue-600" />
                <span>Knowledge graph updated with 23 new connections</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Navigation */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Knowledge Base Manager</h1>
        <div className="flex gap-2">
          <Button 
            variant={viewMode === 'upload' ? 'default' : 'secondary'}
            onClick={() => setViewMode('upload')}
          >
            Upload
          </Button>
          <Button 
            variant={viewMode === 'processing' ? 'default' : 'secondary'}
            onClick={() => setViewMode('processing')}
          >
            Processing ({documents.filter(d => d.status !== 'completed').length})
          </Button>
          <Button 
            variant={viewMode === 'management' ? 'default' : 'secondary'}
            onClick={() => setViewMode('management')}
          >
            Management
          </Button>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'upload' && renderUploadInterface()}
      {viewMode === 'processing' && renderProcessingView()}
      {viewMode === 'management' && renderManagementView()}
    </div>
  );
};

export default EnhancedKnowledgeUploader;